import React, { useState } from "react";
import api from "@/lib/api";
import { Trash2, Trophy, Pencil, Check, Sparkles } from "lucide-react";
import { OverallParticipants } from "./OverallParticipants";
import { OverallAthletePicker } from "./OverallAthletePicker";
import { OverallVoting } from "./OverallVoting";

export const OverallCard = ({ ov, result, cats, athletes, onChanged, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [nome, setNome] = useState(ov.nome);
  const [showCats, setShowCats] = useState(false);
  const participants = result?.participants || [];
  const winnerId = ov.winner_athlete_id;
  const winner = participants.find((p) => p.athlete_id === winnerId);

  const saveName = async () => {
    if (nome.trim() && nome !== ov.nome) await api.put(`/overalls/${ov.id}`, { nome: nome.trim().toUpperCase() });
    setEditing(false);
    onChanged();
  };
  const toggleCat = async (cid) => {
    const ids = ov.category_ids.includes(cid) ? ov.category_ids.filter((x) => x !== cid) : [...ov.category_ids, cid];
    await api.put(`/overalls/${ov.id}`, { category_ids: ids });
    onChanged();
  };
  const add = async (aid) => { await api.post(`/overalls/${ov.id}/participants`, { athlete_id: aid }); onChanged(); };
  const remove = async (aid) => { await api.delete(`/overalls/${ov.id}/participants/${aid}`); onChanged(); };
  const saveVotes = async (votes) => { await api.put(`/overalls/${ov.id}/votes`, { votes }); onChanged(); };
  const setWinner = async (aid) => { await api.put(`/overalls/${ov.id}/winner`, { athlete_id: aid }); onChanged(); };

  return (
    <div className="card p-5" data-testid={`overall-card-${ov.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="overline mb-1 flex items-center gap-2">
            <Trophy size={13} /> Overall {ov.auto && <span className="inline-flex items-center gap-1 text-[#C9A84C]"><Sparkles size={11} /> automático</span>}
          </div>
          {editing ? (
            <div className="flex gap-2 items-center">
              <input className="input-field !py-1 font-display text-xl" value={nome} onChange={(e) => setNome(e.target.value.toUpperCase())} data-testid={`overall-name-input-${ov.id}`} />
              <button type="button" className="btn-primary !py-1 !px-2" onClick={saveName} data-testid={`overall-name-save-${ov.id}`}><Check size={14} /></button>
            </div>
          ) : (
            <div className="font-display text-2xl font-black tracking-tight flex items-center gap-2" data-testid={`overall-name-${ov.id}`}>
              {ov.nome}
              <button type="button" className="btn-ghost !p-1 opacity-60 hover:opacity-100" onClick={() => setEditing(true)} data-testid={`overall-name-edit-${ov.id}`}><Pencil size={12} /></button>
            </div>
          )}
          <button type="button" className="text-xs text-[var(--text-secondary)] mt-1 hover:text-[#C9A84C]" onClick={() => setShowCats((s) => !s)} data-testid={`overall-cats-toggle-${ov.id}`}>
            {ov.category_ids.length} categoria{ov.category_ids.length === 1 ? "" : "s"} · {participants.filter((p) => p.athlete_id).length} atleta{participants.filter((p) => p.athlete_id).length === 1 ? "" : "s"} {showCats ? "▲" : "▼"}
          </button>
        </div>
        <div className="text-right">
          {winner ? (
            <div className="px-3 py-2 border border-[#C9A84C] rounded-sm" data-testid={`overall-winner-${ov.id}`}>
              <div className="overline !text-[#C9A84C]">Campeão Geral</div>
              <div className="font-display font-bold">Nº {winner.numero} · {winner.nome}</div>
              <div className="text-[11px] text-[var(--text-secondary)]">{winner.equipe || winner.categoria}</div>
            </div>
          ) : (
            <div className="text-xs text-[var(--text-secondary)] px-3 py-2 border border-white/10 rounded-sm" data-testid={`overall-no-winner-${ov.id}`}>Campeão ainda não definido</div>
          )}
          <button type="button" className="btn-ghost !p-1.5 mt-2" onClick={onDelete} data-testid={`del-overall-${ov.id}`} title="Excluir Overall"><Trash2 size={14} /></button>
        </div>
      </div>

      {showCats && (
        <div className="mb-4 p-3 border border-white/10 rounded-sm" data-testid={`overall-cats-${ov.id}`}>
          <div className="overline mb-2">Categorias cujos campeões entram neste Overall</div>
          <div className="flex flex-wrap gap-1.5">
            {cats.map((c) => {
              const on = ov.category_ids.includes(c.id);
              return (
                <button key={c.id} type="button" onClick={() => toggleCat(c.id)} data-testid={`overall-cat-${ov.id}-${c.id}`}
                  className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${on ? "border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/10" : "border-white/15 text-[var(--text-secondary)] hover:border-white/40"}`}>
                  {c.nome}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <OverallParticipants overallId={ov.id} participants={participants} winnerId={winnerId} onRemove={remove} />
      <div className="mt-3">
        <OverallAthletePicker athletes={athletes} cats={cats} excludeIds={participants.map((p) => p.athlete_id).filter(Boolean)} onAdd={add} testId={`overall-picker-${ov.id}`} />
        <span className="text-[11px] text-[var(--text-secondary)] ml-3">Ex.: incluir campeão Novice no Overall Open ou campeã Natural no Overall Wellness.</span>
      </div>

      <OverallVoting overallId={ov.id} participants={participants} numArbitros={result?.num_arbitros || 5} votes={ov.votes} winnerId={winnerId} onSaveVotes={saveVotes} onSetWinner={setWinner} />
    </div>
  );
};
